import { z } from 'zod';
import { PERSON_GENDERS } from '../utils/mechitza.js';
const RSVP_STATUSES = ['invited', 'confirmed', 'declined', 'maybe'];
const RSVP_RESPONSE_VALUES = ['PENDING', 'YES', 'NO', 'MAYBE'];
const COUNT = z.number().int().min(0).max(50);
/**
 * A side is one of the values in Settings → `guest_sides` — configurable, so
 * a Bar Mitzvah or a gala is not stuck with "bride" and "groom". The list is
 * checked where it is read (the guest options); here it is a short label.
 */
const SIDE = z.string().trim().min(1).max(100);
/** A tag or dietary need: a value from its Settings list. */
const TAG = z.string().trim().min(1).max(50);
const TAGS = z.array(TAG).max(20);
/** The party counted across male/female/kid counts; missing counts are 0. */
function partyTotal(g) {
    return (g.maleCount ?? 0) + (g.femaleCount ?? 0) + (g.kidCount ?? 0);
}
const AT_LEAST_ONE_PERSON = {
    message: 'A guest must include at least one person',
    path: ['maleCount'],
};
/**
 * An edit that sets all three counts must leave at least one person. One that
 * sets only some cannot be judged here — the others keep their stored values —
 * so the service checks the merged party before writing.
 */
function countsLeaveSomeone(g) {
    return (g.maleCount === undefined ||
        g.femaleCount === undefined ||
        g.kidCount === undefined ||
        partyTotal(g) >= 1);
}
/**
 * Guest fields a client may set from the portal. The event comes from the
 * route, and seating/table assignment stays with the producer — a client adds
 * who is coming, not where they sit.
 */
const portalGuestFields = z.object({
    firstName: z.string().min(1).max(255),
    lastName: z.string().max(255).optional(),
    familyGroup: z.string().max(255).optional(),
    side: SIDE.optional(),
    phone: z.string().max(50).optional(),
    email: z.string().email().max(255).optional(),
    maleCount: COUNT.optional(),
    femaleCount: COUNT.optional(),
    kidCount: COUNT.optional(),
    tags: TAGS.optional(),
    dietaryTags: TAGS.optional(),
    dietaryNotes: z.string().max(2000).optional(),
    notes: z.string().max(2000).optional(),
    address: z.string().max(500).optional(),
});
/**
 * A guest with nobody in it is invisible to seating and to every head count,
 * so a portal guest, like a producer's, has at least one person.
 */
export const portalCreateGuestSchema = portalGuestFields.refine((g) => partyTotal(g) >= 1, AT_LEAST_ONE_PERSON);
/** A portal edit; the handler checks the merged party when only some counts are sent. */
export const portalUpdateGuestSchema = portalGuestFields
    .partial()
    .refine(countsLeaveSomeone, AT_LEAST_ONE_PERSON);
/**
 * Extra emails/phones supplied with a guest. Sent as a replace-all list, so
 * omitting the field leaves existing contacts untouched while `[]` clears them.
 */
export const guestContactSchema = z.object({
    id: z.string().uuid().optional(),
    contactType: z.enum(['EMAIL', 'PHONE']),
    value: z.string().trim().min(1).max(255),
    label: z.string().max(100).nullable().optional(),
    sortOrder: z.number().int().optional(),
});
export const guestContactsSchema = z.array(guestContactSchema).max(20);
/**
 * The named people of a party, sent by the guest form as a replace-all list:
 * a row with an `id` keeps that member, one without is added, and a stored
 * member left out is removed. Omitting the field leaves them untouched.
 */
export const guestPartyMemberInputSchema = z.object({
    id: z.string().uuid().optional(),
    name: z.string().trim().min(1).max(255),
    memberType: z.enum(['ADULT', 'CHILD']).optional(),
    /** Which side of the mechitza an adult sits on; null when not said. */
    gender: z.enum(PERSON_GENDERS).nullable().optional(),
    email: z.string().email().max(255).nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    isPrimary: z.boolean().optional(),
    mealOptionId: z.string().uuid().nullable().optional(),
    dietaryNotes: z.string().max(1000).nullable().optional(),
    attending: z.boolean().nullable().optional(),
});
export const guestPartyMembersSchema = z.array(guestPartyMemberInputSchema).max(30);
/** Everything a producer may set on a new guest, the event aside. */
const guestCreateFields = z.object({
    contacts: guestContactsSchema.optional(),
    partyMembers: guestPartyMembersSchema.optional(),
    firstName: z.string().min(1).max(255),
    lastName: z.string().max(255).optional(),
    familyGroup: z.string().max(255).optional(),
    side: SIDE.optional(),
    phone: z.string().max(50).optional(),
    email: z.string().email().max(255).optional(),
    rsvpStatus: z.enum(RSVP_STATUSES).optional(),
    mealPreference: z.string().max(255).optional(),
    dietaryNotes: z.string().max(2000).optional(),
    dietaryTags: TAGS.optional(),
    tags: TAGS.optional(),
    isVip: z.boolean().optional(),
    tableNumber: z.number().int().min(1).optional(),
    seatNumber: z.number().int().min(1).optional(),
    plusOne: z.boolean().optional(),
    plusOneName: z.string().max(255).optional(),
    maleCount: COUNT.optional(),
    femaleCount: COUNT.optional(),
    kidCount: COUNT.optional(),
    keepTogether: z.boolean().nullable().optional(),
    notes: z.string().max(2000).optional(),
    internalNotes: z.string().max(5000).optional(),
    address: z.string().max(500).optional(),
    sortOrder: z.number().int().optional(),
});
export const createGuestSchema = guestCreateFields
    .extend({ eventId: z.string().uuid() })
    .refine((g) => partyTotal(g) >= 1, {
    message: 'Guest must include at least one person across male/female/kid counts',
    path: ['maleCount'],
});
/** A new guest on the event named in the path (`POST /events/:eventId/guests`). */
export const createEventGuestSchema = guestCreateFields.refine((g) => partyTotal(g) >= 1, {
    message: 'Guest must include at least one person across male/female/kid counts',
    path: ['maleCount'],
});
export const updateGuestSchema = z
    .object({
    contacts: guestContactsSchema.optional(),
    partyMembers: guestPartyMembersSchema.optional(),
    firstName: z.string().min(1).max(255).optional(),
    lastName: z.string().max(255).nullable().optional(),
    familyGroup: z.string().max(255).nullable().optional(),
    side: SIDE.nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    email: z.string().email().max(255).nullable().optional(),
    rsvpStatus: z.enum(RSVP_STATUSES).optional(),
    mealPreference: z.string().max(255).nullable().optional(),
    dietaryNotes: z.string().max(2000).nullable().optional(),
    dietaryTags: TAGS.optional(),
    tags: TAGS.optional(),
    isVip: z.boolean().optional(),
    tableNumber: z.number().int().min(1).nullable().optional(),
    seatNumber: z.number().int().min(1).nullable().optional(),
    plusOne: z.boolean().optional(),
    plusOneName: z.string().max(255).nullable().optional(),
    maleCount: COUNT.optional(),
    femaleCount: COUNT.optional(),
    kidCount: COUNT.optional(),
    keepTogether: z.boolean().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    internalNotes: z.string().max(5000).nullable().optional(),
    address: z.string().max(500).nullable().optional(),
    sortOrder: z.number().int().optional(),
})
    .refine(countsLeaveSomeone, {
    message: 'Guest must include at least one person across male/female/kid counts',
    path: ['maleCount'],
});
export const updateRsvpSchema = z.object({
    rsvpStatus: z.enum(RSVP_STATUSES),
});
/**
 * An answer given on a guest's behalf (`PUT /events/:eventId/guests/:guestId/rsvp`).
 * Written to `GuestRsvp` — the source of truth — and mirrored onto the guest.
 */
export const guestRsvpInputSchema = z.object({
    response: z.enum(RSVP_RESPONSE_VALUES),
    adultCount: COUNT.optional(),
    kidCount: COUNT.optional(),
    notes: z.string().max(2000).nullable().optional(),
});
export const bulkCreateGuestsSchema = z.object({
    guests: z.array(createGuestSchema).min(1).max(500),
});
/**
 * Bulk-update a set of guests with a partial set of field overrides. Used by
 * the guest list's multi-select action bar (Set RSVP, Set Side, etc.). Only
 * fields that make sense to apply uniformly across many rows are exposed.
 */
export const bulkUpdateGuestsSchema = z.object({
    guestIds: z.array(z.string().uuid()).min(1).max(500),
    updates: z
        .object({
        rsvpStatus: z.enum(RSVP_STATUSES).optional(),
        side: SIDE.nullable().optional(),
        familyGroup: z.string().max(255).nullable().optional(),
        tableNumber: z.number().int().min(1).nullable().optional(),
    })
        .refine((u) => u.rsvpStatus !== undefined ||
        u.side !== undefined ||
        u.familyGroup !== undefined ||
        u.tableNumber !== undefined, { message: 'updates must contain at least one field to apply' }),
});
/**
 * The event-scoped bulk edit (`POST /events/:eventId/guests/bulk`): answers go
 * through `GuestRsvp`, tags are added or removed rather than replaced, so one
 * action never wipes labels the rows already carry.
 */
export const bulkEditEventGuestsSchema = z.object({
    guestIds: z.array(z.string().uuid()).min(1).max(500),
    updates: z
        .object({
        response: z.enum(RSVP_RESPONSE_VALUES).optional(),
        side: SIDE.nullable().optional(),
        familyGroup: z.string().max(255).nullable().optional(),
        tableNumber: z.number().int().min(1).nullable().optional(),
        addTags: TAGS.optional(),
        removeTags: TAGS.optional(),
    })
        .refine((u) => Object.values(u).some((value) => value !== undefined), {
        message: 'updates must contain at least one field to apply',
    }),
});
export const bulkDeleteGuestsSchema = z.object({
    guestIds: z.array(z.string().uuid()).min(1).max(500),
});
/** A query value that may arrive once, repeated, or comma-separated. */
const listParam = (item) => z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((raw) => {
    const values = (Array.isArray(raw) ? raw : raw ? [raw] : [])
        .flatMap((value) => value.split(','))
        .map((value) => value.trim())
        .filter(Boolean);
    return values.length ? Array.from(new Set(values)) : undefined;
})
    .pipe(z.array(item).optional());
export const GUEST_SORT_FIELDS = [
    'name',
    'createdAt',
    'table',
    'side',
    'familyGroup',
    'response',
];
/**
 * `GET /events/:eventId/guests` — one page of the event's guest list, the
 * filters applied in the query so the page, the total and the paging agree.
 */
export const guestListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    sort: z.enum(GUEST_SORT_FIELDS).default('name'),
    dir: z.enum(['asc', 'desc']).default('asc'),
    q: z.string().trim().max(200).optional(),
    response: listParam(z.enum(RSVP_RESPONSE_VALUES)),
    side: listParam(z.string().max(100)),
    tags: listParam(z.string().max(50)),
    familyGroup: listParam(z.string().max(255)),
    source: listParam(z.enum(['PRODUCER', 'CLIENT', 'IMPORT', 'PUBLIC_LINK'])),
    seated: z.enum(['yes', 'no']).optional(),
});
/** A seating wish between two guests of one event. */
export const createGuestRelationSchema = z
    .object({
    guestId: z.string().uuid(),
    otherGuestId: z.string().uuid(),
    kind: z.enum(['TOGETHER', 'APART']),
    note: z.string().max(500).nullable().optional(),
})
    .refine((r) => r.guestId !== r.otherGuestId, {
    message: 'A guest cannot be paired with themselves',
    path: ['otherGuestId'],
});
/**
 * Seat parties at a table (`POST /events/:eventId/guests/seat`), or take them
 * off one with `tableNumber: null`. Capacity and shared seats are checked by
 * `SeatAssignmentService`; `allowOverCapacity` is the producer saying yes to a
 * squeezed-in chair.
 */
export const seatEventGuestsSchema = z
    .object({
    guestIds: z.array(z.string().uuid()).min(1).max(500),
    tableNumber: z.number().int().min(1).nullable(),
    seatNumber: z.number().int().min(1).optional(),
    allowOverCapacity: z.boolean().optional(),
})
    .refine((s) => s.seatNumber === undefined || (s.guestIds.length === 1 && s.tableNumber != null), {
    message: 'A particular seat is chosen for one guest at a table',
    path: ['seatNumber'],
});
/** Fold duplicate guests into one (`POST /events/:eventId/guests/merge`). */
export const mergeEventGuestsSchema = z
    .object({
    keepId: z.string().uuid(),
    mergeIds: z.array(z.string().uuid()).min(1).max(20),
})
    .refine((m) => !m.mergeIds.includes(m.keepId), {
    message: 'The guest kept cannot also be merged away',
    path: ['mergeIds'],
});
const IMPORT_ROWS = z.array(z.record(z.unknown())).min(1).max(2000);
/** Check import rows before anything is saved (`POST …/guests/import/preview`). */
export const guestImportPreviewSchema = z.object({ rows: IMPORT_ROWS });
/** Save import rows with a decision each (`POST …/guests/import/commit`). */
export const guestImportCommitSchema = z.object({
    rows: z
        .array(z.object({
        data: z.record(z.unknown()),
        action: z.enum(['add', 'merge', 'skip']),
        mergeInto: z.string().uuid().optional(),
    }))
        .min(1)
        .max(2000)
        .refine((rows) => rows.every((row) => row.action !== 'merge' || !!row.mergeInto), {
        message: 'A merged row names the guest it merges into',
    }),
});
//# sourceMappingURL=guest.js.map